import type { DeepLink } from '@optionway/models';
import { SHORT_LINK_KEY } from '@optionway/models';

interface DeepLinkStore {
  deepLinks: Record<string, DeepLink>;
}

export const useDeepLinkStore = defineStore('deep-link', {
  state: (): DeepLinkStore => ({
    deepLinks: {},
  }),
  getters: {
    // Single responsible place for the shareable URL encoding — re-parses both building blocks
    // so commas (raw in criteriaQueryString, %2C in filterQueryString) are normalized to raw commas.
    fullQueryString(): string {
      const { criteriaQueryString } = useSearchCriteriaStore();
      const { filterQueryString } = useSearchResultFiltersStore();
      const parameters = new URLSearchParams();
      for (const [key, value] of new URLSearchParams(criteriaQueryString)) parameters.append(key, value);
      for (const [key, value] of new URLSearchParams(filterQueryString)) parameters.append(key, value);
      return encodeURI(decodeURIComponent(parameters.toString()));
    },
    hasShortLink(): boolean {
      return !!this.deepLinks[this.fullQueryString];
    },
    shareableUrl(): (origin: string, path: string, queryStringOverride?: string) => string {
      const deepLinks = this.deepLinks;
      return (origin, path, queryStringOverride) => {
        const queryString = queryStringOverride ?? this.fullQueryString;
        const cached = deepLinks[queryString];
        if (cached) return `${origin}${cached.path}?${SHORT_LINK_KEY}=${cached.id}`;
        return `${origin}${path}?${queryString}`;
      };
    },
  },
  actions: {
    // Key by the locally-produced queryString (the same string used for lookup in the
    // getters), never by deepLink.queryString — the backend may normalize/re-encode it, which
    // would make every lookup miss and force the long-URL fallback.
    saveDeepLink(queryString: string, deepLink: DeepLink) {
      this.deepLinks[queryString] = deepLink;
    },
  },
});
